import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Lock, Fingerprint, Delete } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * App Lock Screen — shown when app PIN is enabled and device is locked.
 * Blocks the entire app until PIN is verified.
 */
const AppLockScreen = ({ onUnlock }) => {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [attempts, setAttempts] = useState(0);
  const token = localStorage.getItem('auth_token') || localStorage.getItem('token');

  const handleVerify = useCallback(async (pinValue) => {
    if (pinValue.length < 4) return;
    try {
      const res = await axios.post(`${API}/security/app-pin/verify`, { pin: pinValue }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.success) {
        setPin('');
        setError('');
        onUnlock();
      }
    } catch (e) {
      setAttempts(prev => prev + 1);
      setError('Incorrect PIN');
      setPin('');
      if (attempts >= 4) {
        setError('Too many attempts. Please login again.');
        setTimeout(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('auth_token');
          window.location.href = '/login';
        }, 2000);
      }
    }
  }, [token, onUnlock, attempts]);

  useEffect(() => {
    if (pin.length >= 4 && pin.length <= 6) {
      const t = setTimeout(() => handleVerify(pin), 300);
      return () => clearTimeout(t);
    }
  }, [pin, handleVerify]);

  const handleDigit = (d) => {
    if (pin.length < 6) setPin(prev => prev + d);
  };

  const handleDelete = () => {
    setPin(prev => prev.slice(0, -1));
    setError('');
  };

  // Try biometric first
  const tryBiometric = async () => {
    if (!window.PublicKeyCredential) return;
    try {
      // Simple WebAuthn check - in production this would use full FIDO2
      toast.info('Biometric not configured on this device');
    } catch (e) {
      console.log('Biometric not available');
    }
  };

  const digits = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'bio', '0', 'del'];

  return (
    <div className="fixed inset-0 z-[99999] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-center" data-testid="app-lock-screen">
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
          <Lock className="w-8 h-8 text-white/70" />
        </div>
        <p className="text-white/80 text-sm font-medium">Enter your PIN to unlock</p>
      </div>

      {/* PIN dots */}
      <div className="flex items-center gap-3 mb-8" data-testid="pin-dots">
        {[0, 1, 2, 3, 4, 5].map(i => (
          <div key={i} className={`w-3.5 h-3.5 rounded-full transition-all ${i < pin.length ? 'bg-white scale-110' : 'bg-white/20'} ${i >= 4 ? (pin.length > 4 ? '' : 'hidden') : ''}`} />
        ))}
      </div>

      {error && <p className="text-red-400 text-xs mb-4 animate-pulse" data-testid="pin-error">{error}</p>}

      {/* Numpad */}
      <div className="grid grid-cols-3 gap-4 w-64">
        {digits.map(d => {
          if (d === 'bio') {
            return (
              <button key={d} onClick={tryBiometric} className="w-16 h-16 rounded-full flex items-center justify-center mx-auto active:bg-white/10 transition-colors" data-testid="biometric-btn">
                <Fingerprint className="w-6 h-6 text-white/50" />
              </button>
            );
          }
          if (d === 'del') {
            return (
              <button key={d} onClick={handleDelete} className="w-16 h-16 rounded-full flex items-center justify-center mx-auto active:bg-white/10 transition-colors" data-testid="pin-delete">
                <Delete className="w-5 h-5 text-white/50" />
              </button>
            );
          }
          return (
            <button key={d} onClick={() => handleDigit(d)} className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto text-xl font-medium text-white active:bg-white/25 transition-colors" data-testid={`pin-digit-${d}`}>
              {d}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default AppLockScreen;
